/**
 * SEO helpers for Place pages.
 * Generates deterministic title, description, H1, and JSON-LD
 * so no place page is ever empty or missing meta tags.
 */
import type { FarmlyPlace } from "@/lib/types";

/* ─── Region resolver ─── */

/** Returns best available region string: municipality → county → landskap */
export function resolveRegion(place: FarmlyPlace): string {
  return place.municipality || place.county || place.landskap || "Sverige";
}

/** Returns secondary region for breadcrumb context */
function resolveSecondaryRegion(place: FarmlyPlace): string | null {
  if (place.municipality && place.landskap) return place.landskap;
  if (place.municipality && place.county) return place.county;
  return null;
}

/* ─── Title tag (<60 chars) ─── */

export function buildPlaceTitle(place: FarmlyPlace): string {
  const region = resolveRegion(place);
  const full = `${place.name} – plats och utflyktsmål i ${region}`;
  if (full.length <= 60) return full;

  // Shorter fallback
  const short = `${place.name} – utflyktsmål i ${region}`;
  if (short.length <= 60) return short;

  return `${place.name} – ${region}`;
}

/* ─── Meta description (150–160 chars) ─── */

export function buildPlaceDescription(place: FarmlyPlace): string {
  // Use description_short if available and long enough
  if (place.description_short && place.description_short.length >= 80) {
    return place.description_short.length <= 160
      ? place.description_short
      : place.description_short.slice(0, 157) + "…";
  }

  const region = resolveRegion(place);
  const tagText = place.tags.length > 0
    ? ` Här finns ${place.tags.slice(0, 3).join(", ")}.`
    : "";

  const base = `${place.name} är en plats i ${region} med närhet till natur, utflykter och lokala upplevelser.${tagText} Se karta, tips och platser nära ${place.name}.`;

  return base.length <= 160 ? base : base.slice(0, 157) + "…";
}

/* ─── H1 ─── */

export function buildPlaceH1(place: FarmlyPlace): string {
  const region = resolveRegion(place);
  return `${place.name} – plats i ${region}`;
}

/* ─── About fallback (≥80 words) ─── */

export function buildPlaceAboutFallback(place: FarmlyPlace): string {
  const region = resolveRegion(place);
  const secondary = resolveSecondaryRegion(place);
  const locationContext = secondary
    ? `${place.name} ligger i ${region}, ${secondary}`
    : `${place.name} ligger i ${region}`;

  const tagSentence = place.tags.length > 0
    ? `Platsen erbjuder bland annat ${place.tags.join(", ")}.`
    : "Platsen är en del av det svenska landskapet med närhet till natur och lokala upplevelser.";

  return `${locationContext}. ${tagSentence} Här kan du utforska omgivningarna, hitta tips om vad som finns i närheten och planera ditt nästa besök. Farmly hjälper dig att upptäcka platser, gårdar och upplevelser nära dig – oavsett om du söker en dagsutflykt, en paus längs vägen eller ett nytt favoritställe att återkomma till.`;
}

/* ─── "Besöka {name}" practical text ─── */

export function buildVisitText(place: FarmlyPlace): string {
  const region = resolveRegion(place);
  const parts: string[] = [];

  parts.push(`${place.name} finns i ${region}.`);

  if (place.address_text) {
    parts.push(`Adressen är ${place.address_text}.`);
  }

  if (place.lat && place.lng) {
    parts.push("Se kartan nedan för vägbeskrivning.");
  }

  parts.push(`Planera ditt besök till ${place.name} och upptäck vad som finns i närheten.`);

  return parts.join(" ");
}

/* ─── JSON-LD: schema.org/Place ─── */

export function createPlaceJsonLd(place: FarmlyPlace, canonicalUrl: string): Record<string, unknown> {
  const jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Place",
    "@id": canonicalUrl,
    name: place.name,
    url: canonicalUrl,
  };

  // Description
  const desc = place.description_short || buildPlaceDescription(place);
  jsonLd.description = desc;

  // GeoCoordinates
  if (place.lat && place.lng) {
    jsonLd.geo = {
      "@type": "GeoCoordinates",
      latitude: place.lat,
      longitude: place.lng,
    };
  }

  // PostalAddress
  const hasAddress = place.address_text || place.municipality || place.landskap;
  if (hasAddress) {
    jsonLd.address = {
      "@type": "PostalAddress",
      ...(place.address_text && { streetAddress: place.address_text }),
      ...(place.municipality && { addressLocality: place.municipality }),
      ...(place.landskap && { addressRegion: place.landskap }),
      addressCountry: "SE",
    };
  }

  // Telephone
  if (place.phone) {
    jsonLd.telephone = place.phone;
  }

  return jsonLd;
}

/* ─── Internal link helpers ─── */

export interface RegionLink {
  label: string;
  href: string;
}

export function buildRegionLinks(place: FarmlyPlace): RegionLink[] {
  const links: RegionLink[] = [];

  if (place.municipality) {
    links.push({
      label: `${place.municipality} – platser & utflykter`,
      href: `/explore?municipality=${encodeURIComponent(place.municipality)}`,
    });
  }

  if (place.county) {
    links.push({
      label: `${place.county} – upptäck platser`,
      href: `/explore?county=${encodeURIComponent(place.county)}`,
    });
  } else if (place.landskap) {
    links.push({
      label: `${place.landskap} – upptäck platser`,
      href: `/explore?region=${encodeURIComponent(place.landskap.toLowerCase())}`,
    });
  }

  return links;
}

/* ─── Breadcrumb JSON-LD ─── */

export function buildPlaceBreadcrumbJsonLd(place: FarmlyPlace, canonicalUrl: string): Record<string, unknown> {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://farmly.se";
  const items: Array<{ name: string; url: string }> = [
    { name: "Farmly", url: origin },
    { name: "Utforska", url: `${origin}/explore` },
  ];

  if (place.landskap) {
    items.push({
      name: place.landskap,
      url: `${origin}/explore?region=${encodeURIComponent(place.landskap.toLowerCase())}`,
    });
  }

  items.push({ name: place.name, url: canonicalUrl });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
